import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NgapAct } from '../types/ngap';
import { SimulatorCartItem } from '../features/simulator/types';
import {
  parseCotationSlug,
  findActByCotationSlug,
  generateActPageMeta,
  generateBreadcrumb,
  getActCotationValueForType,
  generateCotationPageUrl
} from '../utils/urlUtils';
import { updatePageMeta, updateJsonLd, generateActSchema } from '../utils/seoUtils';
import { trackPageView } from '../utils/googleAnalytics';
import { trackCotationPageViewed } from '../utils/analyticsEvents';
import { Calendar, Clock, TrendingUp, AlertTriangle, ArrowRight } from 'lucide-react';
import { REVALORIZATION_DATES } from '../constants/dates';

// Components
import ResultsGrid from './ResultsGrid';
import Breadcrumb from './Breadcrumb';
import AppinesBanner from './AppinesBanner';
import Footer from './Footer';
import SeoContentBlock from './SeoContentBlock';
import EmbeddedRevenueSimulator from '../features/simulator/components/EmbeddedRevenueSimulator';

interface CotationValuePageProps {
  acts: NgapAct[];
  cartItems?: SimulatorCartItem[];
  onToggleCartItem?: (act: NgapAct) => void;
  darkMode: boolean;
  displayCotationType?: 'current' | 'july2025' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027';
  onHashtagClick?: (hashtag: string) => void;
  loading: boolean;
}

const CotationValuePage: React.FC<CotationValuePageProps> = ({
  acts,
  cartItems = [],
  onToggleCartItem,
  darkMode,
  onHashtagClick,
  loading
}) => {
  const { cotation_slug_value } = useParams<{ cotation_slug_value: string }>();
  const navigate = useNavigate();
  
  const [foundAct, setFoundAct] = useState<NgapAct | null>(null);
  const [cotationType, setCotationType] = useState<'current' | 'july2025' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'>('current');
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);

  // Déterminer la cotation réellement en vigueur aujourd'hui
  const effectiveCurrentCotationType = React.useMemo(() => {
    if (!foundAct) return 'current';

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const isOnOrAfter = (dateStr: string) => todayStr >= dateStr;

    if (foundAct.revalo_july_2027 && foundAct.cotation_valeur_july_2027 && isOnOrAfter('2027-07-01')) {
      return 'july2027';
    }
    if (foundAct.revalo_sept_2026 && foundAct.cotation_valeur_sept_2026 && isOnOrAfter('2026-09-01')) {
      return 'sept2026';
    }
    if (foundAct.revalo_may_2026 && foundAct.cotation_valeur_may_2026 && isOnOrAfter('2026-05-28')) {
      return 'may2026';
    }
    if (foundAct.revalo_janv_2026 && foundAct.cotation_valeur_janv_2026 && isOnOrAfter('2026-01-01')) {
      return 'janv2026';
    }
    if (foundAct.revalo_july_2025 && foundAct.cotation_valeur_july_2025 && isOnOrAfter('2025-07-01')) {
      return 'july2025';
    }

    return 'current';
  }, [foundAct]);

  // Vérifier si la cotation affichée est obsolète
  const isDisplayedCotationOutdated = React.useMemo(() => {
    if (!foundAct || cotationType === effectiveCurrentCotationType) return false;
    
    // Ordre chronologique des types de cotation
    const cotationOrder = ['current', 'july2025', 'janv2026', 'may2026', 'july2026', 'sept2026', 'july2027'];
    const displayedIndex = cotationOrder.indexOf(cotationType);
    const effectiveIndex = cotationOrder.indexOf(effectiveCurrentCotationType);
    
    // La cotation affichée est obsolète si elle est antérieure à la cotation effective
    return displayedIndex < effectiveIndex;
  }, [cotationType, effectiveCurrentCotationType, foundAct]);

  // Générer l'URL de la cotation actuellement en vigueur
  const effectiveCurrentCotationUrl = React.useMemo(() => {
    if (!foundAct) return null;
    
    return generateCotationPageUrl(foundAct, effectiveCurrentCotationType);
  }, [foundAct, effectiveCurrentCotationType]);

  // Obtenir la valeur de cotation effective
  const effectiveCurrentCotationValue = React.useMemo(() => {
    if (!foundAct) return null;
    
    return getActCotationValueForType(foundAct, effectiveCurrentCotationType);
  }, [foundAct, effectiveCurrentCotationType]);

  // Fonction pour obtenir le label de la date de cotation
  const getCotationDateLabel = (type: string): string => {
    switch (type) {
      case 'current':
        return 'Cotation actuelle';
      case 'janv2026':
        return 'Cotation au 1er janvier 2026';
      case 'may2026':
        return 'Cotation au 28 mai 2026';
      case 'july2026':
        return 'Cotation au 28 mai 2026';
      case 'sept2026':
        return 'Cotation au 1er septembre 2026';
      case 'july2027':
        return 'Cotation au 1er juillet 2027';
      default:
        return 'Cotation';
    }
  };

  // Fonction pour déterminer si la cotation est dans le futur
  const isFutureCotation = (type: string): boolean => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    switch (type) {
      case 'janv2026':
        return todayStr < '2026-01-01';
      case 'may2026':
        return todayStr < '2026-05-28';
      case 'july2025':
        return todayStr < '2025-07-01';
      case 'july2026':
        return todayStr < '2026-05-28';
      case 'sept2026':
        return todayStr < '2026-09-01';
      case 'july2027':
        return todayStr < '2027-07-01';
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!cotation_slug_value || acts.length === 0) return;

    const parsedSlug = parseCotationSlug(cotation_slug_value);

    if (!parsedSlug) {
      navigate('/', { replace: true });
      return;
    }

    const result = findActByCotationSlug(acts, cotation_slug_value);

    if (result && result.act) {
      setFoundAct(result.act);
      setCotationType(result.cotationType);

      // Générer les métadonnées SEO avec URL canonique pointant vers la page datée
      const canonicalPath = `/cotations/${cotation_slug_value}`;
      const meta = generateActPageMeta(result.act, result.cotationType, canonicalPath);
      updatePageMeta(undefined, meta);

      // Update JSON-LD structured data
      const jsonLdSchema = generateActSchema(result.act, result.cotationType);
      updateJsonLd(jsonLdSchema, 'cotation-value-page-jsonld');

      // Track page view only once
      if (!hasTrackedPageView) {
        const cotationLabel = getCotationDateLabel(result.cotationType);
        trackPageView(
          `${result.act.lettre_cle} ${parsedSlug.base_cotation_valeur} - ${cotationLabel}`,
          `/cotations/${cotation_slug_value}`
        );
        trackCotationPageViewed(`${result.act.lettre_cle} ${parsedSlug.base_cotation_valeur}`);
        setHasTrackedPageView(true);
      }
    } else {
      if (parsedSlug.lettre_cle) {
        navigate(`/cotations/${parsedSlug.lettre_cle.toLowerCase()}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [cotation_slug_value, acts, navigate, hasTrackedPageView]);

  // Wrapper pour les clics sur hashtag
  const handleHashtagClickWrapper = (hashtag: string) => {
    if (onHashtagClick) {
      onHashtagClick(hashtag);
    } else {
      navigate(`/cotations/hashtag/${hashtag}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-300">
          Chargement de la cotation...
        </p>
      </div>
    );
  }

  if (!foundAct) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{
        background: darkMode 
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <AppinesBanner className="md:pt-6" />
        
        <div className="pt-20 md:pt-0 px-4 pb-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[]} darkMode={darkMode} />
            
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-orange-400' : 'text-orange-500'
              }`}>
                🚫
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Cotation non trouvée
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                La cotation spécifique demandée n'existe pas ou n'est plus disponible.
              </p>
              
              {/* Actions alternatives */}
              <div className="mt-6 space-y-3">
                {(() => {
                  const parsedSlug = parseCotationSlug(cotation_slug_value || '');
                  const letterKey = parsedSlug?.lettre_cle;
                  
                  return letterKey ? (
                    <div className="space-y-3">
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Mais vous pouvez consulter toutes les cotations <strong>{letterKey}</strong> :
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={() => navigate(`/cotations/${letterKey.toLowerCase()}`)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors font-medium"
                        >
                          Voir toutes les cotations {letterKey}
                        </button>
                        <button
                          onClick={() => navigate('/')}
                          className={`px-6 py-3 rounded-lg transition-colors font-medium ${
                            darkMode 
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Retour à l'accueil
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors font-medium"
                    >
                      Retour à l'accueil
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbItems = generateBreadcrumb(foundAct.lettre_cle, foundAct);
  const cotationLabel = getCotationDateLabel(cotationType);
  const isFuture = isFutureCotation(cotationType);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode 
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />
      
      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb items={breadcrumbItems} darkMode={darkMode} />
          
          {/* Header avec informations sur la cotation */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              {isFuture ? (
                <Clock className="h-8 w-8 mr-3 text-orange-600" />
              ) : cotationType !== 'current' ? (
                <TrendingUp className="h-8 w-8 mr-3 text-green-600" />
              ) : (
                <Calendar className="h-8 w-8 mr-3 text-blue-600" />
              )}
              <h1 className={`text-3xl md:text-4xl font-bold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {foundAct.lettre_cle} {effectiveCurrentCotationValue || parseCotationSlug(cotation_slug_value!)?.base_cotation_valeur}
              </h1>
            </div>
            
            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-4 ${
              isFuture 
                ? 'bg-orange-100 text-orange-800 border border-orange-200'
                : isDisplayedCotationOutdated
                ? 'bg-red-100 text-red-800 border border-red-200'
                : cotationType !== 'current'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-blue-100 text-blue-800 border border-blue-200'
            }`}>
              {isFuture && <Clock className="h-4 w-4 mr-2" />}
              {isDisplayedCotationOutdated && <AlertTriangle className="h-4 w-4 mr-2" />}
              {!isFuture && !isDisplayedCotationOutdated && cotationType !== 'current' && <TrendingUp className="h-4 w-4 mr-2" />}
              {cotationType === 'current' && <Calendar className="h-4 w-4 mr-2" />}
              {isDisplayedCotationOutdated ? 'Cotation obsolète' : cotationLabel}
              {isFuture && ' (À venir)'}
            </div>
            
            <p className={`text-lg ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {foundAct.libelle}
            </p>
            
            {/* Avertissement pour cotation obsolète */}
            {isDisplayedCotationOutdated && effectiveCurrentCotationUrl && effectiveCurrentCotationValue && (
              <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">
                      ⚠️ Cotation obsolète
                    </h3>
                    <p className="text-red-700 mb-3">
                      Cette cotation <strong>{foundAct.lettre_cle} {parseCotationSlug(cotation_slug_value!)?.base_cotation_valeur}</strong> n'est plus en vigueur depuis le {getCotationDateLabel(effectiveCurrentCotationType).toLowerCase().replace('cotation au ', '')}.
                    </p>
                    <p className="text-red-700 mb-4">
                      La cotation actuellement en vigueur est : <strong>{foundAct.lettre_cle} {effectiveCurrentCotationValue}</strong>
                    </p>
                    <a
                      href={effectiveCurrentCotationUrl}
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Voir la cotation en vigueur
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Informations contextuelles sur la cotation */}
            {cotationType !== 'current' && !isDisplayedCotationOutdated && (
              <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                isFuture 
                  ? 'bg-orange-50 border-orange-400'
                  : 'bg-green-50 border-green-400'
              }`}>
                <p className={`text-sm ${
                  isFuture ? 'text-orange-700' : 'text-green-700'
                }`}>
                  {isFuture 
                    ? `Cette cotation entrera en vigueur le ${getCotationDateLabel(cotationType).toLowerCase().replace('cotation au ', '')}.`
                    : `Cette cotation est en vigueur depuis le ${getCotationDateLabel(cotationType).toLowerCase().replace('cotation au ', '')}.`
                  }
                </p>
                
                {/* Lien vers la cotation en vigueur pour les cotations futures */}
                {isFuture && effectiveCurrentCotationUrl && effectiveCurrentCotationValue && (
                  <div className="mt-3">
                    <p className="text-sm text-orange-700 mb-2">
                      Cotation actuellement en vigueur : <strong>{foundAct.lettre_cle} {effectiveCurrentCotationValue}</strong>
                    </p>
                    <a
                      href={effectiveCurrentCotationUrl}
                      className="inline-flex items-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                    >
                      Voir la cotation en vigueur
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Informations pour cotations futures avec lien vers cotation en vigueur */}
          </div>

          {/* Affichage de l'acte */}
          <div className="mb-8">
            <ResultsGrid
              results={[foundAct]}
              cartItems={cartItems}
              onToggleCartItem={onToggleCartItem}
              darkMode={darkMode}
              loading={false}
              displayCotationType={cotationType}
              onHashtagClick={handleHashtagClickWrapper}
              defaultRevaloExpanded
            />
          </div>

          <div className="mb-8">
            <EmbeddedRevenueSimulator act={foundAct} darkMode={darkMode} />
          </div>

          <div className="mb-8">
            <SeoContentBlock act={foundAct} darkMode={darkMode} />
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default React.memo(CotationValuePage);